import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, Send, Megaphone, MessageCircle, Users, Cake, Gift, Trash2 } from "lucide-react";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import { PageLoader, LoadError, EmptyState } from "@/components/admin/LoadState";
import { usePageTitle } from "@/hooks/usePageTitle";
import { fmtNum, plural } from "@/lib/format";

const emptyCamp = { name: "", message: "", channel: "in-app", audience: "all" };
const CHANNEL: Record<string, string> = { "in-app": "Notificación en el portal", whatsapp: "WhatsApp (simulado)" };
const STATUS: Record<string, string> = { borrador: "Borrador", enviada: "Enviada" };
const audienceLabel = (a: string) => (a === "all" ? "Todos los niveles" : `Nivel ${a}`);

export default function AdminCampaigns() {
  usePageTitle("Notificaciones push");
  const [camps, setCamps] = useState<any[]>([]);
  const [birthdays, setBirthdays] = useState<any[]>([]);
  const [bdayInfo, setBdayInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>(emptyCamp);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [tierNames, setTierNames] = useState<string[]>([]);
  const [recipients, setRecipients] = useState<number | null>(null);
  const [toSend, setToSend] = useState<{ camp: any; recipients: number | null } | null>(null);
  const [toDelete, setToDelete] = useState<any>(null);
  const [bdayConfirm, setBdayConfirm] = useState(false);

  const load = () => {
    setFailed(false);
    api.get("/admin/birthdays").then((r) => setBirthdays(r.data)).catch(() => {});
    api.get("/admin/birthday-bonus/info").then((r) => setBdayInfo(r.data)).catch(() => {});
    return api.get("/admin/campaigns").then((r) => setCamps(r.data)).catch(() => setFailed(true)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/tiers").then((r) => setTierNames(r.data.map((t: any) => t.name))).catch(() => {}); }, []);

  // Cuántos clientes recibirán la campaña con la audiencia elegida.
  useEffect(() => {
    if (!open) return;
    setRecipients(null);
    api.get("/admin/campaigns/audience", { params: { audience: form.audience } })
      .then((r) => setRecipients(r.data.recipients)).catch(() => setRecipients(null));
  }, [open, form.audience]);

  const runBirthday = async () => {
    try {
      const { data } = await api.post("/admin/birthday-bonus/run");
      toast.success(data.granted ? `${plural(data.granted, "cliente recibió", "clientes recibieron")} el bono de cumpleaños` : "Nadie recibió el bono: no hay clientes pendientes de premiar este mes.");
      load();
    } catch (err: any) { toast.error(apiError(err)); throw err; }
  };

  const create = async () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = "Escribe un título para la campaña.";
    if (!form.message.trim()) e.message = "Escribe el mensaje que recibirán los clientes.";
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      await api.post("/admin/campaigns", form);
      toast.success("Campaña guardada como borrador. Revísala y pulsa “Enviar” cuando esté lista.");
      setOpen(false); setForm(emptyCamp); load();
    } catch (err: any) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const askSend = async (c: any) => {
    setToSend({ camp: c, recipients: null });
    try {
      const { data } = await api.get("/admin/campaigns/audience", { params: { audience: c.audience } });
      setToSend({ camp: c, recipients: data.recipients });
    } catch { /* se envía igual; el conteo es informativo */ }
  };

  const send = async () => {
    const c = toSend!.camp;
    try {
      const { data } = await api.post(`/admin/campaigns/${c.id}/send`);
      toast.success(`Campaña enviada a ${plural(data.sent, "cliente", "clientes")}${c.channel === "whatsapp" ? " (WhatsApp simulado)" : ""}`);
      load();
    } catch (err: any) { toast.error(apiError(err)); throw err; }
  };

  const remove = async () => {
    try { await api.delete(`/admin/campaigns/${toDelete.id}`); toast.success("Borrador eliminado"); load(); }
    catch (err: any) { toast.error(apiError(err)); throw err; }
  };

  if (loading) return <PageLoader />;

  const pending = birthdays.filter((b) => !b.rewarded && !b.ineligible_reason).length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-campaigns-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-serif font-medium">Notificaciones push</h1>
          <p className="text-sm text-slate-600 mt-1">Envía avisos a los clientes y gestiona el regalo de cumpleaños.</p>
        </div>
        <Button data-testid="new-campaign-button" onClick={() => { setOpen(true); setErrors({}); }} className="gold-gradient-bg text-white font-semibold rounded-full">
          <Plus className="w-4 h-4 mr-1" aria-hidden="true" /> Nueva campaña
        </Button>
      </div>

      <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 flex items-start gap-3">
        <MessageCircle className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" aria-hidden="true" />
        <p className="text-sm text-slate-700">
          <strong>WhatsApp todavía no está conectado.</strong> Los mensajes de ese canal se guardan como notificaciones en el portal del cliente; no llegan a su teléfono.
        </p>
      </div>

      {/* Bono de cumpleaños */}
      <section data-testid="birthday-automation-card" className="card-obsidian p-6" aria-labelledby="bday-title">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-full bg-gold/15 flex items-center justify-center shrink-0">
              <Cake className="w-5 h-5 text-gold-ink" aria-hidden="true" />
            </div>
            <div>
              <h2 id="bday-title" className="text-lg font-semibold">Regalo de cumpleaños</h2>
              <p className="text-sm text-slate-600 mt-1 max-w-xl">
                {bdayInfo?.momento === "dia" ? "El día de su cumpleaños" : "En su mes de cumpleaños"}, la plataforma regala {bdayInfo ? <strong>{fmtNum(bdayInfo.points)} puntos</strong> : "puntos"} a cada cliente.
                Cada cliente lo recibe una sola vez al año, aunque lo ejecutes varias veces. Puedes ejecutarlo ahora si no quieres esperar.
                Se configura en <Link to="/admin/puntos/automaticas" className="text-navy underline">Puntos › Reglas automáticas</Link>.
              </p>
              {bdayInfo && !bdayInfo.enabled && (
                <p className="text-sm text-amber-800 mt-2">La regla de cumpleaños está pausada en Puntos › Reglas automáticas: al ejecutarlo, nadie recibirá puntos.</p>
              )}
            </div>
          </div>
          <Button data-testid="run-birthday-bonus-button" onClick={() => setBdayConfirm(true)} className="gold-gradient-bg text-white font-semibold rounded-full">
            <Gift className="w-4 h-4 mr-1" aria-hidden="true" /> Otorgar bonos ahora
          </Button>
        </div>
        <div className="mt-5 pt-5 border-t border-slate-200">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Cumpleañeros de este mes ({birthdays.length})</h3>
          {birthdays.length === 0 ? (
            <p className="text-sm text-slate-600">Ningún cliente cumple años este mes.</p>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {birthdays.map((b) => (
                <li key={b.id} data-testid="birthday-client" className={`text-sm px-3 py-1.5 rounded-full border flex items-center gap-2 ${b.rewarded ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-800" : b.ineligible_reason ? "bg-slate-50 border-dashed border-slate-300 text-slate-600" : "bg-slate-100 border-slate-200 text-slate-700"}`}>
                  <Cake className="w-3.5 h-3.5" aria-hidden="true" /> {b.name}
                  {b.rewarded ? <span className="text-xs">· ya recibió el regalo</span> : b.ineligible_reason ? <span className="text-xs">· no aplica: {b.ineligible_reason.toLowerCase()}</span> : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <h2 className="text-lg font-semibold">Campañas</h2>
      {failed ? (
        <LoadError what="las campañas" onRetry={load} />
      ) : camps.length === 0 ? (
        <EmptyState icon={Megaphone}>Aún no has creado campañas. Usa “Nueva campaña” para enviar un aviso a tus clientes.</EmptyState>
      ) : (
        <div className="space-y-4">
          {camps.map((c) => (
            <div key={c.id} data-testid="admin-campaign-item" className="card-obsidian p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h3 className="font-semibold text-slate-900">{c.name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${c.channel === "whatsapp" ? "bg-emerald-500/15 text-emerald-800" : "bg-blue-500/15 text-blue-800"}`}>
                      {CHANNEL[c.channel] || c.channel}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${c.status === "enviada" ? "bg-emerald-500/15 text-emerald-800" : "bg-slate-200 text-slate-700"}`}>
                      {STATUS[c.status] || c.status}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600">{c.message}</p>
                  <p className="text-xs text-slate-600 mt-2 flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5" aria-hidden="true" /> {audienceLabel(c.audience)}
                    {c.status === "enviada" && ` · enviada a ${plural(c.sent_count || 0, "cliente", "clientes")}`}
                  </p>
                </div>
                {c.status !== "enviada" && (
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => setToDelete(c)} aria-label={`Eliminar borrador ${c.name}`} className="border-red-500/30 text-red-700 hover:bg-red-500/10 rounded-full h-9">
                      <Trash2 className="w-4 h-4" aria-hidden="true" />
                    </Button>
                    <Button data-testid="send-campaign-button" size="sm" onClick={() => askSend(c)} className="gold-gradient-bg text-white font-semibold rounded-full">
                      <Send className="w-4 h-4 mr-1" aria-hidden="true" /> Enviar
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-obsidian-card border-slate-200 text-slate-900">
          <DialogHeader><DialogTitle className="font-serif text-xl">Nueva campaña</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">La campaña se guarda como borrador. Nadie la recibe hasta que pulses “Enviar”.</p>
            <div>
              <Label htmlFor="camp-name" className="text-slate-700 text-sm">Título *</Label>
              <Input id="camp-name" data-testid="campaign-name-input" value={form.name} onChange={(e: any) => setForm({ ...form, name: e.target.value })} placeholder="Ej: Promo fin de semana" aria-invalid={!!errors.name} aria-describedby={errors.name ? "camp-name-error" : undefined} className="mt-1.5 bg-obsidian border-slate-200 text-slate-900" />
              {errors.name && <p id="camp-name-error" role="alert" className="text-xs text-red-700 mt-1">{errors.name}</p>}
            </div>
            <div>
              <Label htmlFor="camp-message" className="text-slate-700 text-sm">Mensaje *</Label>
              <Textarea id="camp-message" data-testid="campaign-message-input" value={form.message} onChange={(e: any) => setForm({ ...form, message: e.target.value })} aria-invalid={!!errors.message} aria-describedby={errors.message ? "camp-message-error" : undefined} className="mt-1.5 bg-obsidian border-slate-200 text-slate-900" />
              {errors.message && <p id="camp-message-error" role="alert" className="text-xs text-red-700 mt-1">{errors.message}</p>}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="camp-channel" className="text-slate-700 text-sm">Canal</Label>
                <Select value={form.channel} onValueChange={(v: string) => setForm({ ...form, channel: v })}>
                  <SelectTrigger id="camp-channel" data-testid="campaign-channel-select" className="mt-1.5 bg-obsidian border-slate-200 text-slate-900"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-obsidian-card border-slate-200 text-slate-900">
                    <SelectItem value="in-app">Notificación en el portal</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp (simulado)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="camp-audience" className="text-slate-700 text-sm">Audiencia</Label>
                <Select value={form.audience} onValueChange={(v: string) => setForm({ ...form, audience: v })}>
                  <SelectTrigger id="camp-audience" data-testid="campaign-audience-select" className="mt-1.5 bg-obsidian border-slate-200 text-slate-900"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-obsidian-card border-slate-200 text-slate-900">
                    <SelectItem value="all">Todos los niveles</SelectItem>
                    {tierNames.map((t) => <SelectItem key={t} value={t}>Nivel {t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p data-testid="campaign-recipients" className="text-sm text-slate-700 p-3 rounded-lg bg-slate-50 border border-slate-200">
              {recipients === null ? "Calculando destinatarios…" : <>Llegará a <strong>{plural(recipients, "cliente", "clientes")}</strong>: aprobados, no inhabilitados y que aceptaron recibir comunicaciones.</>}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-200 text-slate-700 rounded-full">Cancelar</Button>
            <Button data-testid="save-campaign-button" onClick={create} disabled={saving} className="gold-gradient-bg text-white font-semibold rounded-full">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Guardando" /> : "Guardar borrador"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!toSend}
        onOpenChange={(o) => !o && setToSend(null)}
        testid="send-campaign-dialog"
        title={`¿Enviar “${toSend?.camp.name || ""}”?`}
        description={<>
          <p>
            {toSend?.recipients == null ? "Calculando destinatarios…" : <>La recibirán <strong>{plural(toSend.recipients, "cliente", "clientes")}</strong> ({audienceLabel(toSend.camp.audience).toLowerCase()}) como {CHANNEL[toSend.camp.channel]?.toLowerCase() || "notificación"}.</>}
          </p>
          <p>Una vez enviada no se puede cancelar ni editar.</p>
        </>}
        confirmLabel="Enviar ahora"
        onConfirm={send}
      />

      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        danger
        title="¿Eliminar este borrador?"
        description={<p>“{toDelete?.name}” todavía no se envió. Se eliminará y no se podrá recuperar.</p>}
        confirmLabel="Eliminar borrador"
        onConfirm={remove}
      />

      <ConfirmDialog
        open={bdayConfirm}
        onOpenChange={setBdayConfirm}
        testid="birthday-bonus-dialog"
        title="¿Otorgar los regalos de cumpleaños ahora?"
        description={bdayInfo ? <>
          <p><strong>{plural(bdayInfo.eligible, "cliente recibirá", "clientes recibirán")} {fmtNum(bdayInfo.points)} puntos</strong> y una notificación de felicitación.</p>
          <p>Quien ya recibió el regalo este año no lo vuelve a recibir.</p>
          {!bdayInfo.enabled && <p className="text-amber-800">El regalo está desactivado: nadie recibirá puntos.</p>}
        </> : <p>Se regalarán puntos a quienes cumplen años este mes y aún no recibieron el regalo.</p>}
        confirmLabel={pending || bdayInfo?.eligible ? "Otorgar regalos" : "Ejecutar de todos modos"}
        onConfirm={runBirthday}
      />
    </div>
  );
}
